import type {
  CreateLfgLftPostRequest,
  LfgLftPostResponse,
  UpdateLfgLftPostRequest,
} from "@/shared/api/lfg-lft-types";
import {
  HOUR_SLOT_COUNT,
  LFG_FORMATS,
  LFG_LANGUAGES,
  LFG_RANKS,
  LFG_ROLES,
  LFG_SERVERS,
  isLfgRole,
  slotsToUtcMask,
  utcMaskToLocalRanges,
  type LfgFormat,
  type LfgLanguage,
  type LfgMicFilter,
  type LfgRole,
  type LfgServer,
  type RankRange,
} from "./board-filters";

/** Форма создания и редактирования объявления (KAN-1354). Значения — имена бэкенда. */

export const POST_TYPES = ["Lfg", "Lft"] as const;
export type PostType = (typeof POST_TYPES)[number];

/** Как в макете; бэкенд принимает до 1000, но длинный текст ломает карточку. */
export const DESCRIPTION_MAX = 240;

export interface PostForm {
  type: PostType;
  /** Только у «Ищем игроков»: команда-автор. */
  teamId: string;
  /** Игрок: одна основная роль. Команда: все искомые позиции. */
  roles: LfgRole[];
  /** Только игрок: вторая роль («если не адекват — то саппорт»). */
  secondary: LfgRole | null;
  /** Только команда: диапазон ранга кандидата. У игрока ранг берётся из Riot. */
  rank: RankRange | null;
  mic: LfgMicFilter;
  hourSlots: number[];
  servers: LfgServer[];
  formats: LfgFormat[];
  languages: LfgLanguage[];
  description: string;
}

export function emptyPostForm(type: PostType): PostForm {
  return {
    type,
    teamId: "",
    roles: [],
    secondary: null,
    rank: null,
    mic: "any",
    hourSlots: [],
    servers: [],
    formats: [],
    languages: [],
    description: "",
  };
}

export type PostFormError = "roles" | "team" | "description" | "descriptionTooLong";

export function validatePostForm(f: PostForm): PostFormError | null {
  if (f.roles.length === 0) return "roles";
  if (f.type === "Lft" && !f.teamId) return "team";
  const len = f.description.trim().length;
  if (len === 0) return "description";
  if (len > DESCRIPTION_MAX) return "descriptionTooLong";
  return null;
}

/** Сколько позиций может искать команда (макет: «до 4»). */
export const TEAM_ROLES_MAX = 4;

/**
 * Роль игрока как в макете: первый клик — основная, второй — вторая, третий заменяет вторую.
 * Клик по выбранной снимает её; если снята основная — вторая становится основной.
 */
export function togglePlayerRole(f: PostForm, role: LfgRole): PostForm {
  const primary = f.roles[0] ?? null;
  if (primary === role) return { ...f, roles: f.secondary ? [f.secondary] : [], secondary: null };
  if (f.secondary === role) return { ...f, secondary: null };
  if (primary === null) return { ...f, roles: [role] };
  return { ...f, secondary: role };
}

/** Команда: любое число искомых позиций до потолка; за потолком клик заменяет последнюю выбранную. */
export function toggleTeamRole(f: PostForm, role: LfgRole): PostForm {
  if (f.roles.includes(role)) return { ...f, roles: f.roles.filter((r) => r !== role) };
  const next = f.roles.length >= TEAM_ROLES_MAX ? [...f.roles.slice(0, TEAM_ROLES_MAX - 1), role] : [...f.roles, role];
  return { ...f, roles: next };
}

/** Языки профиля — свободные строки на любом языке («Русский», «English», «ru»): узнаём только Ru/En. */
export function languagesFromProfile(profileLanguages: readonly string[] | null | undefined): LfgLanguage[] {
  const found = new Set<LfgLanguage>();
  for (const raw of profileLanguages ?? []) {
    const v = raw.trim().toLowerCase();
    if (["ru", "russian", "русский", "рус"].includes(v)) found.add("Ru");
    if (["en", "english", "английский", "англ"].includes(v)) found.add("En");
  }
  return LFG_LANGUAGES.filter((l) => found.has(l));
}

type SharedBody = Pick<
  UpdateLfgLftPostRequest,
  "roles" | "secondaryRole" | "mic" | "hoursUtcMask" | "servers" | "languages" | "searchPurposes" | "description"
>;

const MIC_API: Record<LfgMicFilter, string> = { any: "Any", with: "With", without: "Without" };

/** Чем «Ищем игроков» отличается в теле от «Ищу команду»: ранг и вторичная роль. */
function sharedBody(f: PostForm, tzOffsetMin: number): SharedBody {
  return {
    roles: f.type === "Lfg" ? f.roles : LFG_ROLES.filter((r) => f.roles.includes(r)),
    secondaryRole: f.type === "Lfg" ? (f.secondary ?? "") : "",
    mic: MIC_API[f.mic],
    hoursUtcMask: f.hourSlots.length ? slotsToUtcMask(f.hourSlots, tzOffsetMin) : 0,
    servers: LFG_SERVERS.filter((s) => f.servers.includes(s)),
    languages: LFG_LANGUAGES.filter((l) => f.languages.includes(l)),
    searchPurposes: LFG_FORMATS.filter((x) => f.formats.includes(x)),
    description: f.description.trim(),
  };
}

export function toCreateBody(f: PostForm, tzOffsetMin: number): CreateLfgLftPostRequest {
  const shared = sharedBody(f, tzOffsetMin);
  return {
    type: f.type,
    // Старое поле: бэкенд берёт его, только если roles пуст; форма его не оставляет пустым.
    role: f.roles[0] ?? "",
    roles: shared.roles,
    secondaryRole: shared.secondaryRole || undefined,
    mic: shared.mic,
    hoursUtcMask: shared.hoursUtcMask,
    servers: shared.servers,
    languages: shared.languages,
    searchPurposes: shared.searchPurposes,
    description: shared.description ?? "",
    ...(f.type === "Lft"
      ? {
          authorTeamId: f.teamId,
          minRank: f.rank ? LFG_RANKS[f.rank.min] : undefined,
          maxRank: f.rank ? LFG_RANKS[f.rank.max] : undefined,
        }
      : {}),
  };
}

export function toUpdateBody(f: PostForm, tzOffsetMin: number): UpdateLfgLftPostRequest {
  const shared = sharedBody(f, tzOffsetMin);
  return {
    ...shared,
    // Пустая строка — «снять»: бэкенд различает null (не менять) и значение.
    secondaryRole: shared.secondaryRole ?? "",
    ...(f.type === "Lft"
      ? {
          minRank: f.rank ? LFG_RANKS[f.rank.min] : "",
          maxRank: f.rank ? LFG_RANKS[f.rank.max] : "",
        }
      : {}),
  };
}

/** Слот выбран, если в маске покрыты все три его часа в поясе зрителя. */
export function maskToSlots(mask: number, tzOffsetMin: number): number[] {
  if (!mask) return [];
  const ranges = utcMaskToLocalRanges(mask, tzOffsetMin);
  const covered = (h: number) => ranges === null || ranges.some(([a, b]) => (a < b ? h >= a && h < b : h >= a || h < b));
  return [...Array(HOUR_SLOT_COUNT).keys()].filter((s) => [0, 1, 2].every((i) => covered(s * 3 + i)));
}

const rankIndex = (name: string | null | undefined) => {
  const i = LFG_RANKS.findIndex((r) => r.toLowerCase() === (name ?? "").trim().split(/[ _]/)[0]!.toLowerCase());
  return i;
};

/** Объявление → форма для редактирования. */
export function postToForm(post: LfgLftPostResponse, tzOffsetMin: number): PostForm {
  const type: PostType = post.type === "Lft" ? "Lft" : "Lfg";
  const roles = (post.roles ?? [post.role]).filter(isLfgRole);
  const secondary = post.secondaryRole && isLfgRole(post.secondaryRole) ? post.secondaryRole : null;
  const min = rankIndex(post.minRank);
  const max = post.maxRank ? rankIndex(post.maxRank) : LFG_RANKS.length - 1;
  return {
    type,
    teamId: post.author?.type === "Team" ? post.author.id : "",
    roles: type === "Lfg" ? roles.filter((r) => r !== secondary).slice(0, 1) : roles,
    secondary: type === "Lfg" ? secondary : null,
    rank: type === "Lft" && (post.minRank || post.maxRank) && min >= 0 && max >= min ? { min, max } : null,
    mic: post.mic === "With" ? "with" : post.mic === "Without" ? "without" : "any",
    hourSlots: maskToSlots(post.hoursUtcMask ?? 0, tzOffsetMin),
    servers: LFG_SERVERS.filter((s) => (post.servers ?? []).includes(s)),
    formats: LFG_FORMATS.filter((x) => (post.searchPurposes ?? []).includes(x)),
    languages: LFG_LANGUAGES.filter((l) => (post.languages ?? []).includes(l)),
    description: post.description,
  };
}
